import React, { Component, ReactNode, useState, useEffect } from 'react';
import { RefreshCw, Wrench, CheckCircle, AlertCircle, Loader2, Mail, Send, LogIn, ArrowLeft } from '@/shared/ui/icons';
import { supabase } from '../utils/supabase/client';
import { postSupportTicket } from '../services/SupportTicketService';
import { errorFingerprint } from '../utils/errorFingerprint';
import { STALE_RELOAD_FLAG } from '../utils/errorReporter';
import { NativeBridge } from '../services/NativeBridge';
import { RequestAccess } from './RequestAccess';
import { buildAccessRequestTarget } from '../utils/accessRequestTarget';

const SUPPORT_EMAIL = 'support@contractgroup.co.uk';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  /** Keep the surrounding AppLayout chrome and fill only its content outlet. */
  surface?: 'screen' | 'content';
  /** Recover after navigation without remounting healthy page state. */
  resetKey?: string;
}

type ReportStatus = 'idle' | 'reporting' | 'reported' | 'known' | 'fixed' | 'failed';

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string;
  reportStatus: ReportStatus;
  reportedRowId: string | null;
  fixCountdown: number | null;
  githubIssueUrl: string | null;
}

function isStaleDeployError(error: Error | null): boolean {
  if (!error) return false;
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module') ||
    error.message.includes('Unable to preload CSS')
  );
}

const NETWORK_ERROR_PATTERN = /Failed to fetch|NetworkError|Load failed|network request failed/i;

// The browser is offline, OR the caught error looks like a network drop while
// offline. `!navigator.onLine` always wins — a stale-chunk error (new deploy)
// only stays "stale" when the browser reports connectivity; if we're actually
// offline, the same error text means the chunk simply couldn't be fetched.
function isOfflineError(error: Error | null): boolean {
  if (typeof navigator === 'undefined') return false;
  if (!navigator.onLine) {
    if (!error) return true;
    return NETWORK_ERROR_PATTERN.test(error.message) || isStaleDeployError(error);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Error classification taxonomy
// ---------------------------------------------------------------------------
//
// Best-effort heuristic on error text. Render-boundary errors don't always
// carry a clean HTTP status or a structured code — matching on message/name
// substrings is the only signal we reliably have here. That's an acceptable
// tradeoff: even a heuristic match strictly improves on funnelling every
// error into a blanket "Something went wrong", because most of the volume
// (offline, stale deploy, expired session, 403/404/5xx) is unambiguous in
// the message text Supabase/fetch/router code actually throws.
export type ErrorKind =
  | 'offline'
  | 'update'
  | 'session'
  | 'permission'
  | 'notFound'
  | 'server'
  | 'timeout'
  | 'unknown';

// Whole words only: a bare "rls" matched "URLs", "not allowed" matched React's own
// "calling Hooks conditionally, which is not allowed", and a bare 403/404 matched any
// number or module path, so ordinary crashes were shown as "You don't have access".
const SESSION_PATTERN = /\b401\b|unauthori[sz]ed|jwt (expired|malformed)|token.*(expired|invalid)|session.*(expired|missing)|not authenticated|auth session/i;
const PERMISSION_PATTERN = /\b403\b|\bforbidden\b|permission denied|not allowed to (access|view|open|read|edit)|access (is )?denied|row[- ]level security|violates row-level|insufficient privilege|\bRLS\b/i;
const NOT_FOUND_PATTERN = /\b404\b|(page|project|record|document|file|resource) not found|does not exist|no rows returned/i;
const SERVER_PATTERN = /\b50\d\b|internal server|server error|service unavailable|bad gateway|gateway timeout|upstream/i;
// React and bundler failures are code faults, never a statement about the user's access.
const CODE_FAULT_PATTERN = /react\.dev\/(link|errors)|Minified React error|Rules of Hooks|Should have a queue|Invalid hook call|is not a function|is not defined|Cannot read properties of (undefined|null)|Failed to fetch dynamically imported module/i;

function structuredKind(error: Error): ErrorKind | null {
  const shape = error as Error & { status?: unknown; statusCode?: unknown; code?: unknown };
  const status = Number(shape.status ?? shape.statusCode);
  if (status === 401) return 'session';
  if (status === 403) return 'permission';
  if (status === 404) return 'notFound';
  if (status >= 500 && status < 600) return 'server';
  // Postgres insufficient_privilege and PostgREST's JWT/role refusals.
  if (shape.code === '42501' || shape.code === 'PGRST301' || shape.code === 'PGRST302') return 'permission';
  return null;
}
const TIMEOUT_PATTERN = /timeout|timed out|took too long|deadline exceeded|aborted/i;

// Precedence matters: most specific / most confidently-user-side first, so a
// message that happens to contain multiple signals (e.g. a 401 wrapped in a
// generic "Failed to fetch") resolves to the more actionable classification.
export function classifyError(error: Error | null): ErrorKind {
  if (isOfflineError(error)) return 'offline';
  if (isStaleDeployError(error) && typeof navigator !== 'undefined' && navigator.onLine) return 'update';

  const text = `${error?.name ?? ''} ${error?.message ?? ''}`;
  if (!error) return 'unknown';

  const structured = structuredKind(error);
  if (structured) return structured;
  if (CODE_FAULT_PATTERN.test(text)) return 'unknown';

  if (SESSION_PATTERN.test(text)) return 'session';
  if (PERMISSION_PATTERN.test(text)) return 'permission';
  if (NOT_FOUND_PATTERN.test(text)) return 'notFound';
  if (SERVER_PATTERN.test(text)) return 'server';
  if (TIMEOUT_PATTERN.test(text)) return 'timeout';
  return 'unknown';
}

export interface ErrorKindConfig {
  kind: ErrorKind;
  title: string;
  message: string;
  actionLabel: string;
  /** true = this is a failure the user can't self-resolve, so offer the manual
   *  "Report this issue" button. It used to also fire the auto-fix pipeline;
   *  automatic reporting is disabled, so it is now purely about what we offer. */
  report: boolean;
  /** true = show the prominent "open a support ticket" flow (unknowns only). */
  escalate: boolean;
  /** true = show a subtle "still stuck? contact support" text link. */
  subtleSupport: boolean;
}

/** The per-kind copy. Exported so a surface that is NOT a crash — a dead URL,
 *  say — can show the same words without dragging in the reporting pipeline
 *  behind them. One place to edit what each failure says. */
export function getErrorKindConfig(kind: ErrorKind): ErrorKindConfig {
  switch (kind) {
    case 'offline':
      return {
        kind,
        title: 'No connection',
        message: 'Check your internet connection and try again.',
        actionLabel: 'Try again',
        report: false,
        escalate: false,
        subtleSupport: false,
      };
    case 'update':
      return {
        kind,
        title: 'A new version is ready',
        message: 'Reload to get the latest.',
        actionLabel: 'Reload now',
        report: false,
        escalate: false,
        subtleSupport: false,
      };
    case 'session':
      return {
        kind,
        title: 'Session expired',
        message: 'Please sign in again to continue.',
        actionLabel: 'Sign in',
        report: false,
        escalate: false,
        subtleSupport: false,
      };
    case 'permission':
      return {
        kind,
        title: "You don't have access",
        message: "You don't have permission to view this. Ask an admin if you think that's wrong.",
        actionLabel: 'Go back',
        report: true,
        escalate: false,
        subtleSupport: true,
      };
    case 'notFound':
      return {
        kind,
        title: 'Not found',
        message: 'This may have been moved or deleted.',
        actionLabel: 'Go back',
        report: true,
        escalate: false,
        subtleSupport: true,
      };
    case 'server':
      return {
        kind,
        title: 'Server problem',
        message: 'Our servers hit a snag — try again in a moment.',
        actionLabel: 'Try again',
        report: true,
        escalate: false,
        subtleSupport: true,
      };
    case 'timeout':
      return {
        kind,
        title: 'This timed out',
        message: 'The request took too long. Try again.',
        actionLabel: 'Try again',
        report: true,
        escalate: false,
        subtleSupport: false,
      };
    case 'unknown':
    default:
      return {
        kind: 'unknown',
        title: 'Something went wrong',
        message: 'Try again. Your saved work is unaffected.',
        actionLabel: 'Try again',
        report: true,
        escalate: true,
        subtleSupport: false,
      };
  }
}

// ---------------------------------------------------------------------------
// Status banner shown below the main error message
// ---------------------------------------------------------------------------

// Quiet single-line status instead of a prominent coloured box.
//
// With automatic reporting disabled the boundary leaves reportStatus at 'idle',
// so in practice this renders nothing. The non-idle branches are kept because
// they still describe real states if reporting is ever turned back on, and
// because 'idle' returning null is what makes the crash screen fall through to
// the manual "Report this issue" flow.
function StatusBanner({ status, countdown }: { status: ReportStatus; countdown: number | null }) {
  if (status === 'idle') return null;

  const styles: React.CSSProperties = {
    marginTop: '14px',
    fontSize: '12.5px',
    lineHeight: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    color: 'var(--muted-foreground, #6b7280)',
  };

  if (status === 'reporting') {
    return (
      <div style={styles}>
        <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
        <span>Reporting this to our team…</span>
      </div>
    );
  }

  if (status === 'reported') {
    return (
      <div style={styles}>
        <Wrench size={12} style={{ flexShrink: 0 }} />
        <span>Auto-reported — this page reloads itself when a fix ships.</span>
      </div>
    );
  }

  if (status === 'known') {
    return (
      <div style={styles}>
        <Wrench size={12} style={{ flexShrink: 0 }} />
        <span>Already being worked on — this page reloads itself when it's resolved.</span>
      </div>
    );
  }

  if (status === 'fixed') {
    return (
      <div style={styles}>
        <CheckCircle size={12} style={{ flexShrink: 0, color: 'var(--status-success, #16a34a)' }} />
        <span>Fixed — reloading in {countdown ?? 5}s.</span>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div style={styles}>
        <AlertCircle size={12} style={{ flexShrink: 0 }} />
        <span>Couldn't auto-report this. Contact support if it keeps happening.</span>
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Full-page error UI
// ---------------------------------------------------------------------------

function FullPageError({
  error,
  errorInfo,
  errorId,
  reportStatus,
  fixCountdown,
  onRetry,
  componentName,
  surface = 'screen',
}: {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string;
  reportStatus: ReportStatus;
  fixCountdown: number | null;
  onRetry: () => void;
  componentName?: string;
  surface?: 'screen' | 'content';
  /** Recover after navigation without remounting healthy page state. */
  resetKey?: string;
}) {
  const [reportOpen, setReportOpen] = useState(false);
  const [userDescription, setUserDescription] = useState('');
  const [reportSendStatus, setReportSendStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const kind = classifyError(error);
  const kindConfig = getErrorKindConfig(kind);
  const isOffline = kind === 'offline';
  // Only a genuine refusal of an addressable page can be asked for.
  const accessTarget = kind === 'permission' && typeof window !== 'undefined'
    ? buildAccessRequestTarget(window.location.pathname, { reference: errorId })
    : null;

  const buildMailto = () => {
    const subject = `Quantis support ticket ${errorId}`;
    const body = [
      `Ticket reference: ${errorId}`,
      `Category: ${kind}`,
      `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      '',
      'What I was doing:',
      userDescription || '',
      '',
      '— technical details —',
      `Error: ${error?.message ?? 'Unknown'}`,
    ].join('\n');
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSendReport = async () => {
    setReportSendStatus('sending');
    try {
      const res = await postSupportTicket({
        ticketRef:       errorId,
        category:        'bug',
        errorKind:       kind,
        priority:        'medium',
        subject:         `[${kind}] ${(error?.message ?? 'Unknown error').slice(0, 110)}`,
        errorMessage:    error?.message ?? 'Unknown error',
        errorName:       error?.name,
        errorStack:      error?.stack,
        componentStack:  errorInfo?.componentStack ?? undefined,
        componentName:   componentName,
        url:             typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent:       typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        buildId:         typeof __APP_BUILD_INFO__ !== 'undefined' ? __APP_BUILD_INFO__.buildId : undefined,
        userDescription: userDescription.trim() || undefined,
      });
      if (!res) throw new Error('Not signed in, ticket not sent');
      if (!res.ok) throw new Error(`Support ticket endpoint returned ${res.status}`);
      setReportSendStatus('sent');
    } catch (err) {
      console.warn('Support ticket submission failed:', err);
      setReportSendStatus('failed');
    }
  };

  const [isHovering, setIsHovering] = useState(false);
  const [isPressing, setIsPressing] = useState(false);

  // Connectivity drops aren't bugs — recover on our own once the browser
  // reports we're back online, no user action needed.
  useEffect(() => {
    if (!isOffline) return;
    const handleOnline = () => onRetry();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isOffline, onRetry]);

  // ⌘R (or Ctrl+R) triggers the same primary action as the button.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        handlePrimaryAction();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, onRetry]);

  // Per-kind primary action. Stale deploys need a real page load to fetch
  // new chunks; session expiry needs to actually clear the dead session
  // before landing back on sign-in (a plain retry would just re-throw the
  // same auth error); permission/not-found favour a real "back" navigation
  // when there's history to go to. Everything else reuses in-place retry.
  const handlePrimaryAction = () => {
    if (kind === 'update') {
      window.location.reload();
      return;
    }
    if (kind === 'session') {
      supabase.auth.signOut({ scope: 'local' }).finally(() => {
        window.location.href = '/';
      });
      return;
    }
    if ((kind === 'permission' || kind === 'notFound') && typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
      return;
    }
    onRetry();
  };

  const headline = kindConfig.title;
  const subtext = kindConfig.message;
  const actionLabel = kindConfig.actionLabel;
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const showKeyboardShortcut = typeof window !== 'undefined'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isContentSurface = surface === 'content';

  return (
    <div
      style={{
        minHeight: isContentSurface ? '100%' : '100dvh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--background, #f9fafb)',
        fontFamily: 'var(--font-apple, -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif)',
        padding: '24px',
        position: 'relative',
      }}
    >
      {!isContentSurface && <div
        style={{
          position: 'absolute',
          top: 'max(20px, env(safe-area-inset-top, 0px))',
          left: 'max(24px, env(safe-area-inset-left, 0px))',
          fontFamily: 'var(--font-apple, -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif)',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--foreground, #111)',
          letterSpacing: '-0.02em',
          opacity: 0.55,
        }}
      >
        Quantis
      </div>}

      <div
        style={{
          maxWidth: '420px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          animation: 'qs-error-rise 620ms cubic-bezier(0.16, 1, 0.3, 1) both',
        }}
      >
        {/* Focal visual — a calm, hand-drawn mark rather than a stock icon-in-a-circle.
            Each state gets its own bespoke inline SVG so the glyph itself carries the
            meaning (no background chip, no alarm colours except a light stale accent). */}
        <div
          style={{
            marginBottom: '28px',
            display: 'flex',
            animation: 'qs-error-icon-in 700ms cubic-bezier(0.16, 1, 0.3, 1) 80ms both',
          }}
        >
          {kind === 'offline' && (
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 30C24 24 30 21 36 21C42 21 48 24 54 30" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.35" strokeWidth="2.5" strokeLinecap="round" />
              <path d="M24 38C27.5 34.5 31.5 32.5 36 32.5C40.5 32.5 44.5 34.5 48 38" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.55" strokeWidth="2.5" strokeLinecap="round" />
              <path d="M30 46C32 44 34 43 36 43C38 43 40 44 42 46" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.75" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="36" cy="53" r="2.6" fill="var(--muted-foreground, #6b7280)" />
              <line x1="16" y1="18" x2="56" y2="56" stroke="var(--foreground, #111)" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          )}
          {kind === 'update' && (
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M50 26C46.6 21.2 41.1 18 35 18C24.5 18 16 26.5 16 37C16 47.5 24.5 56 35 56C43.9 56 51.3 49.9 53.4 41.6"
                stroke="var(--brand, #5d147c)"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path d="M50 15V27H38" stroke="var(--brand, #5d147c)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="35" cy="37" r="3.2" fill="var(--brand, #5d147c)" />
            </svg>
          )}
          {kind === 'session' && (
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Open door + arrow — session lapsed, walk back in */}
              <path d="M28 16H44V56H28" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M28 16L18 20V52L28 56" stroke="var(--brand, #5d147c)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="24" cy="37" r="1.8" fill="var(--brand, #5d147c)" />
              <path d="M40 28L52 37L40 46" stroke="var(--brand, #5d147c)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="40" y1="37" x2="52" y2="37" stroke="var(--brand, #5d147c)" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          )}
          {kind === 'permission' && (
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Padlock — closed off, not broken */}
              <rect x="21" y="33" width="30" height="24" rx="5" stroke="var(--brand, #5d147c)" strokeOpacity="0.9" strokeWidth="2.5" />
              <path d="M27 33V25C27 18.4 31.9 14 36 14C40.1 14 45 18.4 45 25V33" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.5" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="36" cy="43" r="2.6" fill="var(--brand, #5d147c)" />
              <line x1="36" y1="45.5" x2="36" y2="50" stroke="var(--brand, #5d147c)" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          )}
          {kind === 'notFound' && (
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Magnifier over a dashed void — looked, not there */}
              <circle cx="31" cy="31" r="15" stroke="var(--brand, #5d147c)" strokeOpacity="0.9" strokeWidth="2.5" />
              <path d="M24 31C24 27.1 27.1 24 31 24" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.4" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="1 5" />
              <line x1="41.5" y1="41.5" x2="55" y2="55" stroke="var(--foreground, #111)" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="26" y1="31" x2="36" y2="31" stroke="var(--brand, #5d147c)" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          )}
          {kind === 'server' && (
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Stacked server racks with a fault mark — our side, not the user's */}
              <rect x="18" y="18" width="36" height="14" rx="3" stroke="var(--brand, #5d147c)" strokeOpacity="0.9" strokeWidth="2.5" />
              <rect x="18" y="40" width="36" height="14" rx="3" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.45" strokeWidth="2.5" />
              <circle cx="25" cy="25" r="1.8" fill="var(--brand, #5d147c)" />
              <circle cx="25" cy="47" r="1.8" fill="var(--muted-foreground, #6b7280)" />
              <line x1="32" y1="25" x2="44" y2="25" stroke="var(--brand, #5d147c)" strokeOpacity="0.6" strokeWidth="2" strokeLinecap="round" />
              <line x1="32" y1="47" x2="44" y2="47" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.4" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
          {kind === 'timeout' && (
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Clock with a stalled hand — transient, just took too long */}
              <circle cx="36" cy="38" r="20" stroke="var(--brand, #5d147c)" strokeOpacity="0.9" strokeWidth="2.5" />
              <path d="M36 25V38L45 44" stroke="var(--brand, #5d147c)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M28 14H44" stroke="var(--muted-foreground, #6b7280)" strokeOpacity="0.45" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          )}
          {kind === 'unknown' && (
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M36 14L61 54H11L36 14Z"
                stroke="var(--brand, #5d147c)"
                strokeOpacity="0.9"
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              <line x1="36" y1="30" x2="36" y2="41" stroke="var(--brand, #5d147c)" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="36" cy="47.5" r="1.8" fill="var(--brand, #5d147c)" />
            </svg>
          )}
        </div>

        <h1 style={{
          fontSize: '26px',
          fontWeight: 600,
          color: 'var(--foreground, #111)',
          margin: '0 0 10px',
          letterSpacing: '-0.4px',
          lineHeight: 1.25,
        }}>
          {headline}
        </h1>
        <p style={{
          fontSize: '15px',
          color: 'var(--muted-foreground, #6b7280)',
          lineHeight: 1.55,
          margin: '0',
          maxWidth: '320px',
        }}>
          {subtext}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginTop: '32px', width: '100%' }}>
          {accessTarget && <RequestAccess target={accessTarget} audience={accessTarget.projectSlug || accessTarget.projectId ? 'project' : 'workspace'} />}
          <button
            onClick={handlePrimaryAction}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => { setIsHovering(false); setIsPressing(false); }}
            onMouseDown={() => setIsPressing(true)}
            onMouseUp={() => setIsPressing(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
              padding: '10px 22px',
              borderRadius: '999px',
              background: accessTarget ? 'transparent' : isPressing ? 'var(--brand-strong, #4a1063)' : 'var(--brand, #5d147c)',
              color: accessTarget ? 'var(--muted-foreground, #6b7280)' : '#fff',
              fontFamily: 'inherit',
              fontSize: '14.5px',
              fontWeight: 600,
              letterSpacing: '-0.1px',
              border: accessTarget ? '1px solid var(--border, #e5e7eb)' : 'none',
              cursor: 'pointer',
              // Flat, as the rest of the neutral palette: no coloured glow or lift.
              transform: isPressing ? 'scale(0.98)' : 'none',
              boxShadow: 'none',
              filter: !accessTarget && isHovering && !isPressing ? 'brightness(1.08)' : 'none',
              transition: 'transform 160ms cubic-bezier(0.16, 1, 0.3, 1), filter 120ms ease, background 120ms ease',
            }}
          >
            {kind === 'session' ? (
              <LogIn size={15} strokeWidth={2.25} style={{ transform: isHovering ? 'translateX(2px)' : 'translateX(0px)', transition: 'transform 420ms cubic-bezier(0.16, 1, 0.3, 1)' }} />
            ) : kind === 'permission' || kind === 'notFound' ? (
              <ArrowLeft size={15} strokeWidth={2.25} style={{ transform: isHovering ? 'translateX(-2px)' : 'translateX(0px)', transition: 'transform 420ms cubic-bezier(0.16, 1, 0.3, 1)' }} />
            ) : (
              <RefreshCw size={15} strokeWidth={2.25} style={{ transform: isHovering ? 'rotate(50deg)' : 'rotate(0deg)', transition: 'transform 420ms cubic-bezier(0.16, 1, 0.3, 1)' }} />
            )}
            {actionLabel}
          </button>

          {showKeyboardShortcut && !accessTarget && (
            <span style={{
              fontSize: '12px',
              color: 'var(--muted-foreground, #9ca3af)',
              opacity: 0.7,
              letterSpacing: '0.01em',
            }}>
              or press {isMac ? '⌘R' : 'Ctrl+R'}
            </span>
          )}
        </div>

        {/* Reporting progress. Renders nothing while automatic reporting is
            disabled (reportStatus stays 'idle'); the gate is left in place so
            self-fix kinds like offline/update/session could never show it. */}
        {kindConfig.report && !reportOpen && reportSendStatus !== 'sent' && (
          <StatusBanner status={reportStatus} countdown={fixCountdown} />
        )}

        {/* Support entry point. Genuine unknowns get a clear, standing
            invitation to open a ticket — everything else is confidently
            self-resolvable, so at most a quiet text link for the "could be
            a mis-config" kinds (permission/notFound/server). Self-fix kinds
            (offline/update/session) show nothing. */}
        {!reportOpen && reportSendStatus !== 'sent' && (kindConfig.escalate || kindConfig.subtleSupport) && (
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            style={{
              marginTop: kindConfig.escalate ? '18px' : '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: 0,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: kindConfig.escalate ? '13.5px' : '12px',
              fontWeight: kindConfig.escalate ? 600 : 500,
              color: kindConfig.escalate ? 'var(--brand, #5d147c)' : 'var(--muted-foreground, #9ca3af)',
              opacity: kindConfig.escalate ? 1 : 0.85,
              textDecoration: 'none',
            }}
          >
            {kindConfig.escalate && <Mail size={13} />}
            {kindConfig.escalate ? 'Report this issue' : 'Still stuck? Contact support'}
          </button>
        )}

        {!reportOpen && kindConfig.report && errorId && (
          <span style={{
            marginTop: '10px',
            fontSize: '11px',
            color: 'var(--muted-foreground, #9ca3af)',
            opacity: 0.65,
            letterSpacing: '0.02em',
          }}>
            Reference {errorId}
          </span>
        )}

        {reportOpen && reportSendStatus !== 'sent' && (
          <div
            style={{
              marginTop: '20px',
              width: '100%',
              maxWidth: '440px',
              padding: '16px',
              borderRadius: '12px',
              background: 'var(--card, #fff)',
              border: '1px solid var(--border, #e5e7eb)',
              textAlign: 'left',
            }}
          >
            <label
              htmlFor="support-ticket-description"
              style={{
                display: 'block',
                fontFamily: 'inherit',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--foreground, #111)',
                marginBottom: '6px',
              }}
            >
              What were you doing? <span style={{ color: 'var(--muted-foreground, #9ca3af)', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              id="support-ticket-description"
              value={userDescription}
              onChange={(e) => setUserDescription(e.target.value)}
              placeholder="e.g. Saving an interim valuation when the page froze."
              rows={3}
              autoFocus
              disabled={reportSendStatus === 'sending'}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--border, #e5e7eb)',
                fontFamily: 'inherit',
                fontSize: '13px',
                lineHeight: 1.5,
                color: 'var(--foreground, #111)',
                background: 'var(--background, #fff)',
                resize: 'vertical',
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
              {reportSendStatus === 'failed' && (
                <a
                  href={buildMailto()}
                  style={{
                    fontSize: '12px',
                    color: 'var(--brand, #5d147c)',
                    textDecoration: 'none',
                    marginRight: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Mail size={12} />
                  Couldn't send. Email instead?
                </a>
              )}
              <button
                type="button"
                onClick={() => {
                  setReportOpen(false);
                  setReportSendStatus('idle');
                }}
                disabled={reportSendStatus === 'sending'}
                style={{
                  padding: '8px 14px',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--muted-foreground, #6b7280)',
                  fontSize: '13px',
                  fontWeight: 500,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendReport}
                disabled={reportSendStatus === 'sending'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  background: 'var(--brand, #5d147c)',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: reportSendStatus === 'sending' ? 'wait' : 'pointer',
                  opacity: reportSendStatus === 'sending' ? 0.7 : 1,
                }}
              >
                {reportSendStatus === 'sending' ? (
                  <>
                    <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                    Opening…
                  </>
                ) : (
                  <>
                    <Send size={13} />
                    Open ticket
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {reportSendStatus === 'sent' && (
          <div
            style={{
              marginTop: '20px',
              width: '100%',
              maxWidth: '440px',
              padding: '12px 16px',
              borderRadius: '10px',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#166534',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            <CheckCircle size={16} style={{ flexShrink: 0 }} />
            <span>Ticket submitted — we'll follow up if we need more details.</span>
          </div>
        )}
      </div>

      {/* Spin keyframe for the reporting spinner, plus a gentle entrance for the card and icon */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes qs-error-rise {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes qs-error-icon-in {
          from { opacity: 0; transform: scale(0.92) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
        }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error boundary class
// ---------------------------------------------------------------------------

export class ErrorBoundary extends Component<Props, State> {
  private _pollInterval: ReturnType<typeof setInterval> | null = null;
  private _countdownInterval: ReturnType<typeof setInterval> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      reportStatus: 'idle',
      reportedRowId: null,
      fixCountdown: null,
      githubIssueUrl: null,
    };
  }

  componentDidMount() {
    window.addEventListener('quantis:native:error-retry', this._handleNativeRecovery as EventListener);
  }

  componentDidUpdate(previousProps: Props) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this._stopPolling();
      if (this._countdownInterval) clearInterval(this._countdownInterval);
      this._clearNativeError();
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: '',
        reportStatus: 'idle',
        reportedRowId: null,
        fixCountdown: null,
        githubIssueUrl: null,
      });
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: errorFingerprint(error.name, error.message),
    };
  }

  private _notifyNativeError(error: Error, kind = classifyError(error)) {
    if (!NativeBridge.isIOSShell()) return;
    const config = getErrorKindConfig(kind);
    NativeBridge.send('webAppError', {
      visible: true,
      kind,
      title: config.title,
      message: config.message,
      actionLabel: config.actionLabel,
      referenceCode: this.state.errorId,
      supportAvailable: config.escalate || config.subtleSupport,
    });
  }

  private _clearNativeError() {
    if (NativeBridge.isIOSShell()) {
      NativeBridge.send('webAppError', { visible: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (error.message.includes('cssRules') || error.message.includes('stylesheet')) return;

    this.setState({ errorInfo });

    console.error(`Error in ${this.props.componentName || 'Component'}:`, error, errorInfo);

    // Stale deployment — auto-reload once regardless of report policy, since
    // this is the one case where reloading itself (not just showing UI) is
    // the fix. Kept ahead of the classifier gate below intentionally.
    if (isStaleDeployError(error) && typeof navigator !== 'undefined' && navigator.onLine) {
      // Shared with errorReporter's handleStaleDeployment: its silent reload
      // stamps this flag too, so the two guards count as ONE auto-reload and
      // a still-broken deploy lands on the "new version" screen instead of
      // earning a second boundary-driven reload.
      const RELOAD_FLAG = STALE_RELOAD_FLAG;
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        setTimeout(() => window.location.reload(), 1500);
        return;
      }
      // If we already reloaded once this session and still hitting 404s,
      // show the recovery UI so the user can act.
      this._notifyNativeError(error, 'update');
      return;
    }

    // Classify, then only fire the auto-fix report pipeline for kinds the
    // user can't self-resolve (server/timeout/permission/notFound/unknown).
    // Self-fix kinds — offline, update, session — never hit the DB: the
    // fallback UI already handles them on its own (online-listener retry,
    // reload, sign-out-and-redirect respectively).
    const kind = classifyError(error);
    this._notifyNativeError(error, kind);

    // AUTOMATIC REPORTING IS DISABLED.
    //
    // This used to upsert into support_tickets via record_error_occurrence,
    // which fired the autofix agent. A report now only exists if a human filed
    // it — either through "Report a problem or suggest an improvement" in the
    // support modal, or through the "Report this issue" button on this very
    // screen (handleSendReport → POST /support-tickets). reportStatus stays
    // 'idle', which renders no banner and leaves that manual flow in place.
    //
    // The DB side is off too (triggers + cron dropped, record_error_occurrence
    // is a no-op) — see migration
    // 20260821120000_disable_error_autofix_and_auto_reporting.sql. Both halves
    // are needed: this one stops new builds filing, the DB one stops builds
    // already out in the wild.

  }

  // _startPolling / _startFixCountdown are gone with the autofix agent: they
  // watched a support_tickets row flip to 'fixed' and then reloaded the page
  // under the user. Nothing flips that status automatically any more.
  private _stopPolling() {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = null;
    }
  }

  componentWillUnmount() {
    this._stopPolling();
    if (this._countdownInterval) clearInterval(this._countdownInterval);
    window.removeEventListener('quantis:native:error-retry', this._handleNativeRecovery as EventListener);
  }

  private _handleNativeRecovery = (event: CustomEvent<{ action?: string }>) => {
    const action = event.detail?.action;
    this._stopPolling();
    if (this._countdownInterval) clearInterval(this._countdownInterval);
    this._clearNativeError();

    if (action === 'signIn') {
      void supabase.auth.signOut({ scope: 'local' }).finally(() => window.location.assign('/'));
      return;
    }
    if (action === 'back') {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: '',
        reportStatus: 'idle',
        reportedRowId: null,
        fixCountdown: null,
        githubIssueUrl: null,
      }, () => {
        if (window.history.length > 1) window.history.back();
        else window.location.assign('/');
      });
      return;
    }
    window.location.reload();
  };

  handleRetry = () => {
    this._stopPolling();
    if (this._countdownInterval) clearInterval(this._countdownInterval);
    this._clearNativeError();
    // Stale-deploy errors need a real page load to fetch the new chunks. For
    // everything else, just clear the error so the boundary re-mounts the failed
    // subtree in place — a full window.location.reload() drops the user back to
    // the app's entry route (the current view isn't in the URL on the native
    // shell), which reads as "going back a page".
    if (isStaleDeployError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      reportStatus: 'idle',
      reportedRowId: null,
      fixCountdown: null,
      githubIssueUrl: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      if (!this.props.componentName) {
        return (
          <FullPageError
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            errorId={this.state.errorId}
            reportStatus={this.state.reportStatus}
            fixCountdown={this.state.fixCountdown}
            onRetry={this.handleRetry}
            componentName={this.props.componentName}
            surface={this.props.surface}
          />
        );
      }

      // Inline boundary for a named sub-component
      return (
        <div style={{
          padding: '20px',
          borderRadius: '10px',
          border: '1px solid var(--status-danger-border)',
          background: 'var(--status-danger-bg)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <p style={{ fontWeight: 500, color: 'var(--status-danger-foreground)', margin: 0 }}>
            {this.props.componentName} couldn't load
          </p>
          <p style={{ fontSize: '13px', color: 'var(--muted-foreground)', margin: 0 }}>
            {this.state.reportStatus === 'known'
              ? 'Our team is already working on this.'
              : this.state.reportStatus === 'reported' || this.state.reportStatus === 'fixed'
              ? 'This has been reported and a fix is on the way.'
              : 'Try refreshing. If it keeps happening, quote the reference code to support.'}
          </p>
          <button
            onClick={this.handleRetry}
            style={{
              alignSelf: 'flex-start',
              marginTop: '4px',
              padding: '6px 14px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'transparent',
              fontSize: '13px',
              cursor: 'pointer',
              color: 'var(--foreground)',
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
